/**
 * Truck Search Select Component
 * Takes user input and fetches matching trucks asynchronously.
 * @param {function} onSelect - Callback function when a truck is selected
 * @param {string} carrier - Carrier filter for fetching trucks
 * @param {object} selectedTruck - Currently selected truck object {value, label}
 * @param {boolean} required - If true, makes the selection required
 * @param {boolean} location - Includes location in the label if true
 */

import React, { useMemo } from "react";
import AsyncSelect from "react-select/async";
import instance from "../../Interceptor/api_instance";
import debounce from "lodash.debounce";
import { customStyles } from "../Utility/ReactSelectStyles";
import { useTranslation } from "react-i18next";

const TruckSearchSelect = ({
  onSelect,
  carrier,
  selectedTruck,
  required = true,
  location = true,
  enableAll = false,
  styles = customStyles
}) => {
  const { t } = useTranslation();
  const filterOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 1) return [];

    try {
      const res = await instance.get(
        `assets/get-trucks-out?q=${inputValue}&carrier=${carrier}&is_active=${true}`
      );
      return res.data.map((truck) => ({
        value: truck.id,
        label: `${truck.name} ${
          location ? truck.location?.loc_name || "" : ""
        }`,
        licensePlate: truck.license_plate,
        isDisabled: truck.location && !enableAll ? true : false,
        truckRepairs: truck.open_repairs || [],
      }));
    } catch (err) {
      console.error("Failed to fetch trucks: ", err);
      return [];
    }
  };

  // ✅ Debounce filterOptions and return a wrapped function that resolves a Promise
  const debouncedLoadOptions = useMemo(() => {
    const wrapped = debounce((inputValue, callback) => {
      filterOptions(inputValue).then(callback);
    }, 1000); // 500ms debounce

    return (inputValue) =>
      new Promise((resolve) => wrapped(inputValue, resolve));
  }, [carrier]);

  return (
    <AsyncSelect
      key={carrier || "no-carrier"}
      aria-label="Truck"
      inputId="truck-select"
      name="truck"
      defaultOptions={false}
      loadingMessage={() => t("general.loading")}
      loadOptions={debouncedLoadOptions}
      onChange={(option) => onSelect(option)}
      placeholder={t("yard-tables.table-columns.truck")}
      noOptionsMessage={() => t("forms.type.vehicle.search.truck.not-found")}
      isClearable
      className="search-select"
      styles={styles}
      unstyled={true}
      value={selectedTruck}
      required={required}
    />
  );
};

export default React.memo(TruckSearchSelect);
